import { useCallback, useMemo } from 'react';

import {
  type PermissionsMachineImplementations,
  useInitPermissionsMachine,
  usePermissionsMachine,
} from '@sg/shared-fe/features/permissions';

import { useTelemetry } from '@order/Telemetry';

import { usePushNotificationPermissionPrimerEnabled } from '../usePushNotificationPermissionPrimerEnabled';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Drives the push notification permission primer flow for a specific entry
 * point. `variants` is the FF allow-list this consumer responds to.
 *
 * Returns the flags + handlers the consumer needs to decide whether to render
 * a loading screen, the primer, or its normal content.
 */
export const usePushNotificationPermissionPrimer = (params: Params) => {
  const { variants } = params;

  const { track } = useTelemetry();
  const { canEnablePushNotificationPrimer, pushNotificationPrimerVariant } =
    usePushNotificationPermissionPrimerEnabled();

  // ─────────────────────────────────────────────────────────────────────

  const isPushNotificationPrimerEnabled =
    canEnablePushNotificationPrimer &&
    (pushNotificationPrimerVariant === 'disabled'
      ? false
      : variants.includes(pushNotificationPrimerVariant));

  // ─── Telemetry ───────────────────────────────────────────────────────

  const entryPoint = VARIANT_ENTRY_POINT[pushNotificationPrimerVariant];

  const telemetryPayload = useMemo(
    () => ({ entry_point: entryPoint, variant: pushNotificationPrimerVariant }),
    [entryPoint, pushNotificationPrimerVariant],
  );

  const onPushNotificationPrimerShow = useCallback(() => {
    track('push_notification_primer_shown', telemetryPayload);
  }, [track, telemetryPayload]);

  const onRequestPushNotificationPermission = useCallback(() => {
    track('push_notification_primer_cta_tapped', telemetryPayload);
  }, [track, telemetryPayload]);

  const onSkipPushNotificationPermission = useCallback(() => {
    track('push_notification_primer_dismissed', telemetryPayload);
  }, [track, telemetryPayload]);

  const implementations = useMemo<PermissionsMachineImplementations>(
    () => ({
      actions: {
        onPushNotificationPrimerShow,
        onRequestPushNotificationPermission,
        onSkipPushNotificationPermission,
      },
    }),
    [
      onPushNotificationPrimerShow,
      onRequestPushNotificationPermission,
      onSkipPushNotificationPermission,
    ],
  );

  // ─── Actor ───────────────────────────────────────────────────────────

  const permissionsActorRef = useInitPermissionsMachine({
    isPushNotificationPrimerEnabled,
    isLocationPrimerEnabled: false,
    implementations,
  });

  const {
    isPushNotificationPermissionsLoading,
    isPushNotificationPrimerVisible,
    isRequestingPushNotificationPermission,
    isSkippingPushNotificationPermission,
    isPushNotificationPermissionDenied,
    isPushNotificationPermissionSkipped,
    isPushNotificationPermissionAlreadyShown,
    requestPushNotificationPermissions,
    skipPushNotificationPermissions,
  } = usePermissionsMachine(permissionsActorRef);

  // ─── Derived ─────────────────────────────────────────────────────────

  const isPrimerActive =
    isPushNotificationPrimerVisible ||
    isRequestingPushNotificationPermission ||
    isSkippingPushNotificationPermission;

  // ─────────────────────────────────────────────────────────────────────

  return {
    isPushNotificationPrimerEnabled,
    isPushNotificationPermissionsLoading,
    isPrimerActive,
    isRequestingPushNotificationPermission,
    isSkippingPushNotificationPermission,
    isPushNotificationPermissionDenied,
    isPushNotificationPermissionSkipped,
    isPushNotificationPermissionAlreadyShown,
    onAllow: requestPushNotificationPermissions,
    onSkip: skipPushNotificationPermissions,
  };
};

// ─── Constants ───────────────────────────────────────────────────────────────

const VARIANT_ENTRY_POINT = {
  disabled: undefined,
  A: 'onboarding',
  B: 'location-search',
  C: 'order-confirmation',
} as const;

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  variants: Exclude<
    ReturnType<typeof usePushNotificationPermissionPrimerEnabled>['pushNotificationPrimerVariant'],
    'disabled'
  >[];
};
